/** RFC 4122 v4 UUID using crypto.getRandomValues when available. */
export function uuid(): string {
	const cryptoObj = (globalThis as any).crypto as Crypto | undefined;
	if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
		return cryptoObj.randomUUID();
	}
	const buf = new Uint8Array(16);
	if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
		cryptoObj.getRandomValues(buf);
	} else {
		for (let i = 0; i < 16; i++) buf[i] = Math.floor(Math.random() * 256);
	}
	buf[6] = (buf[6] & 0x0f) | 0x40;
	buf[8] = (buf[8] & 0x3f) | 0x80;
	const hex: string[] = [];
	for (let i = 0; i < 16; i++) hex.push(buf[i].toString(16).padStart(2, '0'));
	return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

/** Trailing-edge throttle: invoke fn at most once every `ms` ms. */
export function throttle<F extends (...args: any[]) => void>(fn: F, ms: number): F {
	let last = 0;
	let pending: ReturnType<typeof setTimeout> | null = null;
	let pendingArgs: any[] | null = null;
	return function (this: unknown, ...args: any[]) {
		const now = Date.now();
		const remaining = ms - (now - last);
		if (remaining <= 0) {
			last = now;
			fn.apply(this, args);
		} else {
			pendingArgs = args;
			if (!pending) {
				pending = setTimeout(() => {
					last = Date.now();
					pending = null;
					if (pendingArgs) fn.apply(this, pendingArgs);
					pendingArgs = null;
				}, remaining);
			}
		}
	} as F;
}

/** Stable, fast non-cryptographic hash. djb2. */
export function hashCode(str: string): string {
	let h = 5381;
	for (let i = 0; i < str.length; i++) {
		h = ((h << 5) + h + str.charCodeAt(i)) | 0;
	}
	return (h >>> 0).toString(36);
}

export function now(): number {
	return Math.floor(Date.now() / 1000);
}

export function nowMs(): number {
	return Date.now();
}

export function safeAssign<T extends object>(base: T, ...sources: Partial<T>[]): T {
	for (const src of sources) {
		if (!src) continue;
		for (const key of Object.keys(src) as (keyof T)[]) {
			const val = src[key];
			if (val !== undefined) (base as any)[key] = val;
		}
	}
	return base;
}
